/**
 * @coeldery/locale-switcher — **語言切換（細掣 ＋ 選單）**
 *
 * 為何要模組：每個多語言產品都要「右上角切語言」嘅同一個表面（第二個 app 用得到）→ 依 R3 做模組。
 * 為何係細掣＋選單（而非一排掣）：產品負責人 2026-09-21 手機實測 —— 一排 3 粒掣喺窄螢幕會
 *   **wrap 落第二行**（`shell__header` 係 flex-wrap）→ 直接搶走 header 空間；細掣佔位固定。
 *
 * ⭐ **零 i18n 依賴**：語言名由呼叫方用 `labels` 傳入（本模組唔識 i18next）。
 *
 * 規格（跟 `ui-spec.md` §七 無障礙）：
 *   · 原生 `<button>`（鍵盤可操作）＋ `aria-haspopup="listbox"`／`aria-expanded`
 *   · 選單 `role="listbox"`／`role="option"`＋`aria-selected`；**Esc** 或點外面收
 *   · 熱區 ≥36px（header 次要動作；主要 CTA 另有 44px 規範）
 *   · 顏色一律 CSS var（`--color-*`）
 */
import { useEffect, useRef, useState } from 'react';

export interface LocaleOption {
  /** 語言碼（例 `zh-HK`）—— 呼叫方自己定語意 */
  code: string;
  /** 顯示名（例「繁體中文（香港）」）—— 由呼叫方提供（本模組唔做翻譯） */
  label: string;
}

export interface LocaleSwitcherProps {
  locales: LocaleOption[];
  current: string;
  onChange: (code: string) => void;
  /** 掣面顯示（例：`{ 'zh-HK': '中', en: 'EN', 'zh-CN': '簡' }`）；唔傳就用 `code` */
  shortLabels?: Record<string, string>;
  /** 無障礙名（例「語言」）—— 呼叫方傳已翻譯字串 */
  ariaLabel?: string;
}

export default function LocaleSwitcher({
  locales,
  current,
  onChange,
  shortLabels,
  ariaLabel,
}: LocaleSwitcherProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  /** Esc 收選單（鍵盤可操作） */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const face = shortLabels?.[current] ?? current;

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: '0 0 auto' }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          minHeight: '36px',
          minWidth: '48px',
          padding: '0 10px',
          borderRadius: '999px',
          border: '1.5px solid var(--color-divider)',
          background: 'var(--color-card)',
          color: 'var(--color-text)',
          fontFamily: 'inherit',
          fontSize: '14px',
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        {face}
        <span aria-hidden="true" style={{ fontSize: '11px', opacity: 0.7 }}>
          ▾
        </span>
      </button>

      {open ? (
        <>
          {/* 點外面收（透明覆蓋層；唔搶 focus） */}
          <div onClick={() => setOpen(false)} aria-hidden="true" style={{ position: 'fixed', inset: 0, zIndex: 35 }} />
          <ul
            role="listbox"
            aria-label={ariaLabel}
            style={{
              position: 'absolute',
              right: 0,
              top: 'calc(100% + 6px)',
              zIndex: 40,
              margin: 0,
              padding: '6px',
              listStyle: 'none',
              minWidth: '160px',
              background: 'var(--color-card)',
              border: '1px solid var(--color-divider)',
              borderRadius: '14px',
              boxShadow: 'var(--shadow-modal, var(--shadow-soft, none))',
            }}
          >
            {locales.map((l) => {
              const active = l.code === current;
              return (
                <li key={l.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    lang={l.code}
                    onClick={() => {
                      onChange(l.code);
                      setOpen(false);
                    }}
                    style={{
                      width: '100%',
                      minHeight: '40px',
                      textAlign: 'left',
                      padding: '8px 10px',
                      border: 'none',
                      borderRadius: '10px',
                      background: active ? 'var(--color-primary-soft, var(--color-off-bg))' : 'transparent',
                      color: 'var(--color-text)',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      fontWeight: active ? 800 : 600,
                      cursor: 'pointer',
                    }}
                  >
                    {l.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}
